import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowLeft, Save, CheckCircle2, Loader2, Plus, Trash2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';

const EMPTY = {
  country: '', income_type: '', gross_income: '', foreign_tax_paid: '',
  currency: '', exchange_rate_source: '', description: '',
};

export default function ForeignIncomeForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const onInput = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  useEffect(() => {
    if (!clientId) return;
    onboardingAPI.getSection(clientId, 'foreign_income')
      .then((res) => setItems(res.data.response_data?.foreign_income || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  const persist = (next, status) => onboardingAPI.saveSection(clientId, 'foreign_income', {
    response_data: { foreign_income: next }, status,
  });

  const addItem = () => {
    if (!draft.country.trim() || !draft.income_type.trim() || !draft.gross_income || !draft.currency.trim()) {
      toast.error('Country, income type, gross income and currency are required');
      return;
    }
    const next = [...items, draft];
    setItems(next); setDraft(EMPTY); setShowForm(false);
    persist(next, 'in_progress').catch(() => toast.error('Failed to save'));
  };
  const removeItem = (i) => { const next = items.filter((_, idx) => idx !== i); setItems(next); persist(next, 'in_progress'); };

  const finalize = async (status) => {
    setSaving(true);
    try { await persist(items, status); toast.success(status === 'completed' ? 'Foreign income saved' : 'Saved'); navigate('/onboarding'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Foreign Income</CardTitle>
          <CardDescription>Add income you've earned overseas, including any foreign tax paid.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-3 border rounded-lg p-3">
              <Globe className="w-5 h-5 text-foreground" />
              <div className="flex-1">
                <div className="font-medium text-foreground">{it.country} — {it.income_type}</div>
                <div className="text-xs text-muted-foreground">gross {it.gross_income} {it.currency} · foreign tax {it.foreign_tax_paid || '—'}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => removeItem(i)} title="Remove"><Trash2 className="w-4 h-4 text-red-500" /></Button>
            </div>
          ))}

          {showForm ? (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Country *</Label><Input value={draft.country} onChange={onInput('country')} data-testid="fi-country" /></div>
                <div className="space-y-2"><Label>Income type *</Label><Input value={draft.income_type} onChange={onInput('income_type')} placeholder="e.g. employment, rental, dividends" data-testid="fi-type" /></div>
                <div className="space-y-2"><Label>Gross income *</Label><Input type="number" value={draft.gross_income} onChange={onInput('gross_income')} data-testid="fi-gross" /></div>
                <div className="space-y-2"><Label>Foreign tax paid</Label><Input type="number" value={draft.foreign_tax_paid} onChange={onInput('foreign_tax_paid')} /></div>
                <div className="space-y-2"><Label>Currency *</Label><Input value={draft.currency} onChange={onInput('currency')} placeholder="e.g. USD, GBP" data-testid="fi-currency" /></div>
                <div className="space-y-2"><Label>Exchange rate source</Label><Input value={draft.exchange_rate_source} onChange={onInput('exchange_rate_source')} /></div>
                <div className="space-y-2 md:col-span-2"><Label>Description</Label><Input value={draft.description} onChange={onInput('description')} /></div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setShowForm(false); setDraft(EMPTY); }}>Cancel</Button>
                <Button onClick={addItem} data-testid="fi-add-submit"><Plus className="w-4 h-4 mr-1" /> Add foreign income</Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setShowForm(true)} data-testid="fi-add-btn"><Plus className="w-4 h-4 mr-2" /> Add foreign income</Button>
          )}

          <p className="text-[11px] text-muted-foreground/70">Supporting documents (foreign tax returns, statements, certificates) will be available in the Document Centre.</p>

          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => finalize('in_progress')} disabled={saving}><Save className="w-4 h-4 mr-2" /> Save &amp; return later</Button>
            <Button onClick={() => finalize('completed')} disabled={saving} data-testid="fi-complete"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
